import { useCallback, useEffect, useMemo } from 'react';
import useUppy from '@medflex/hooks/useUppy';
import { EVENTS, triggerToast } from '@medflex/components/stories/Toast';
import useUserSession from '@medflex/hooks/useUserSession';

// eslint-disable-next-line import/prefer-default-export
export const useFileInput = ({ endpoint, dropTarget, restrictions, onSuccess, onError }) => {
  const { session } = useUserSession();

  const handleFileError = (_, { message }) => {
    triggerToast(EVENTS.ALERT, {
      title: message,
      options: {
        toastId: 'fileRestrictionCheckFailed',
      },
    });
  };

  const {
    errors: fileErrors,
    files,
    removeFile,
    register: registerFileInput,
    retry,
    upload,
    uploading,
  } = useUppy({
    id: 'uppy-xhr',
    uploader: 'xhr',
    dropTarget,
    endpoint,
    headers: {
      AUTHORIZATION: `Bearer ${session.access_token || ''}`,
    },
    restrictions,
    multiple: false,
    onRestrictionFailed: handleFileError,
  });

  const retryFailedFiles = useCallback(
    () => Promise.all(fileErrors.map(error => retry(error.file))),
    [fileErrors, retry],
  );

  const fileUploadCompleted = useMemo(() => {
    if (!uploading && files.length > 0) {
      if (!files.some(({ progress }) => !progress?.uploadComplete)) {
        return true;
      }
    }
    return false;
  }, [files, uploading]);

  const uploadWithRetry = useCallback(async () => {
    if (fileErrors.length > 0) {
      await retryFailedFiles();
      return;
    }
    const uploadResult = await upload();
    if (uploadResult.failed.length > 0) {
      onError();
    }
  }, [fileErrors, retryFailedFiles, upload, onError]);

  useEffect(() => {
    if (fileUploadCompleted) onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadCompleted]);

  return {
    registerFileInput,
    files,
    upload: uploadWithRetry,
    uploading,
    fileUploadCompleted,
    removeFile,
  };
};
